import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Fab,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { Add as AddIcon, Label as LabelIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePasskey } from '../contexts/PasskeyContext';
import { getUserThoughts, getUserLabels, createThought, updateThought, Thought } from '../services/thoughtService';
import ThoughtCard from '../components/ThoughtCard';
import Header from '../components/Header';
import Sidebar, { MenuSection } from '../components/Sidebar';

const Home: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser } = useAuth();
  const { passkey } = usePasskey();
  const navigate = useNavigate();
  const location = useLocation();
  const [thoughts, setThoughts] = useState<Thought[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [selectedSection, setSelectedSection] = useState<MenuSection>('thoughts');
  const [selectedLabel, setSelectedLabel] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    if (currentUser && passkey) {
      loadThoughts();
      loadLabels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, passkey, selectedLabel, refreshKey]);

  // Refresh data when navigating back to home
  useEffect(() => {
    if (location.pathname === '/' && currentUser) {
      setRefreshKey(prev => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const loadThoughts = async () => {
    if (!currentUser || !passkey) return;
    
    try {
      setLoading(true);
      const filterLabel = selectedLabel === 'all' ? null : selectedLabel;
      const fetchedThoughts = await getUserThoughts(
        currentUser.uid,
        passkey,
        filterLabel
      );
      setThoughts(fetchedThoughts);
    } catch (error) {
      console.error('Error loading thoughts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLabels = async () => {
    if (!currentUser) return;
    
    try {
      const fetchedLabels = await getUserLabels(currentUser.uid);
      setLabels(fetchedLabels);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  };

  const handleCreateThought = async () => {
    if (!currentUser || !passkey) {
      console.error('Cannot create thought: missing user or passkey');
      return;
    }
    
    try {
      const thoughtId = await createThought(currentUser.uid, {
        title: 'New Thought',
        content: '',
        labels: []
      }, passkey);
      navigate(`/thought/${thoughtId}`, {
        state: {
          initialTitle: 'New Thought',
          initialContent: '',
          initialLabels: []
        }
      });
    } catch (error) {
      console.error('Error creating thought:', error);
    }
  };

  const handleSectionSelect = (section: MenuSection) => {
    setSelectedSection(section);
    if (section === 'diary') {
      navigate('/diary');
    } else if (section === 'journals') {
      navigate('/journals');
    } else if (section === 'download') {
      navigate('/download');
    }
  };

  const handleLabelChange = (event: SelectChangeEvent<string>) => {
    setSelectedLabel(event.target.value);
  };

  const handleLabelsUpdate = async (thoughtId: string, newLabels: string[]) => {
    if (!passkey) return;
    
    try {
      await updateThought(thoughtId, { labels: newLabels }, passkey);
      loadThoughts();
      loadLabels();
    } catch (error) {
      console.error('Error updating labels:', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <>
      <Header 
        onMenuClick={toggleSidebar}
        showMenuButton={isMobile}
      />
      <Box sx={{ display: 'flex', mt: '64px' }}>
        <Sidebar
          open={sidebarOpen}
          selectedSection={selectedSection}
          onSectionSelect={handleSectionSelect}
          onClose={() => setSidebarOpen(false)}
        />
        
        <Box 
          component="main"
          sx={{ 
            flexGrow: 1,
            p: 3,
            width: '100%',
          }}
        >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                Thoughts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quick notes and ideas
              </Typography>
            </Box>
            
            {/* Labels Dropdown */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="label-filter-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LabelIcon sx={{ fontSize: 18 }} />
                  Filter by Label
                </Box>
              </InputLabel>
              <Select
                labelId="label-filter-label"
                id="label-filter"
                value={selectedLabel}
                label="Filter by Label"
                onChange={handleLabelChange}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="all">
                  <em>All Thoughts</em>
                </MenuItem>
                {labels.map((label) => (
                  <MenuItem key={label} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedLabel !== 'all' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="primary">
                Showing thoughts labeled: <strong>{selectedLabel}</strong>
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : thoughts.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 2
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {selectedLabel === 'all' ? 'No thoughts yet' : `No thoughts with label "${selectedLabel}"`}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedLabel === 'all' 
                  ? 'Click the + button to create your first thought'
                  : 'Try selecting a different label or create a new thought'}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {thoughts.map((thought) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={thought.id}>
                  <ThoughtCard 
                    thought={thought}
                    availableLabels={labels}
                    onLabelsUpdate={handleLabelsUpdate}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <Fab
            color="primary"
            aria-label="add thought"
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              width: 56,
              height: 56,
            }}
            onClick={handleCreateThought}
          >
            <AddIcon sx={{ fontSize: 28 }} />
          </Fab>
        </Box>
      </Box>
    </>
  );
};

export default Home;
